<template>
  <div class="admin-releases">
    <!-- Tab Bar -->
    <div class="admin-releases__tabs">
      <hs-tabs :tabs="tabs" v-model="activeTab" />
    </div>

    <!-- Releases Tab -->
    <div v-show="activeTab === 'releases'" class="admin-releases__body">
      <!-- Releases Toolbar -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex flex-wrap items-center gap-3">
          <hs-button
            variant="primary"
            icon="upload"
            @click="showUploadModal = true"
          >
            New Release
          </hs-button>
          <hs-button
            variant="secondary"
            icon="refresh-cw"
            :loading="loadingReleases"
            @click="refreshData"
          >
            Refresh
          </hs-button>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <hs-select
            v-model="selectedProduct"
            class="min-w-[170px]"
            :options="productFilterOptions"
            placeholder="All Products"
            @update:modelValue="onProductChange"
          />
          <hs-select
            v-model="selectedFlavor"
            class="min-w-[160px]"
            :options="flavorFilterOptions"
            placeholder="All Flavors"
            @update:modelValue="onFlavorChange"
          />
          <hs-select
            v-model="selectedPlatform"
            class="min-w-[190px]"
            :options="platformOptions"
            placeholder="All Platforms"
            @update:modelValue="onPlatformChange"
          />
        </div>
      </div>
      <section class="admin-releases__stats">
        <hs-metric-card
          v-for="stat in summaryStats"
          :key="stat.label"
          :label="stat.label"
          :value="stat.value"
          :icon="stat.icon"
          :tone="stat.tone"
        />
      </section>

      <!-- Releases Table -->
      <div
        class="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="overflow-x-auto">
          <table class="w-full table-fixed text-left text-sm">
            <thead
              class="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
            >
              <tr>
                <th
                  class="w-[9%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Version
                </th>
                <th
                  class="w-[9%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Platform
                </th>
                <th
                  class="w-[13%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Target
                </th>
                <th
                  class="w-[9%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Status
                </th>
                <th
                  class="w-[9%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Required
                </th>
                <th
                  class="w-[19%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  File
                </th>
                <th
                  class="w-[7%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Size
                </th>
                <th
                  class="w-[12%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Created
                </th>
                <th
                  class="w-[10%] px-4 py-3 font-medium text-gray-600 dark:text-gray-300"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingReleases">
                <td colspan="9" class="px-4 py-8 text-center text-gray-500">
                  <vue-feather
                    type="loader"
                    size="20"
                    class="animate-spin inline-block"
                  />
                  <span class="ml-2">Loading releases...</span>
                </td>
              </tr>
              <tr v-else-if="releases.length === 0">
                <td colspan="9" class="px-4 py-8 text-center text-gray-500">
                  No releases found. Upload one to get started.
                </td>
              </tr>
              <tr
                v-for="release in releases"
                :key="release.id"
                class="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
              >
                <td
                  class="px-4 py-3 font-mono font-medium text-gray-900 dark:text-white"
                >
                  {{ release.version }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="platformBadgeClass(release.platform)"
                  >
                    {{ release.platform }}
                  </span>
                  <span
                    v-if="!release.has_artifact"
                    class="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                    title="Store release (no artifact)"
                  >
                    Store
                  </span>
                </td>
                <td class="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">
                  <span class="font-medium">{{ targetLabel(release) }}</span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="
                      release.is_published
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    "
                  >
                    {{ release.is_published ? "Published" : "Draft" }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    v-if="release.is_required"
                    class="text-red-600 dark:text-red-400 font-medium text-xs"
                  >
                    Required
                  </span>
                  <span v-else class="text-gray-400 text-xs">Optional</span>
                </td>
                <td
                  class="px-4 py-3 text-gray-600 dark:text-gray-300 text-xs truncate max-w-[200px]"
                >
                  {{
                    release.artifact_original_name ||
                    (release.has_artifact ? "—" : "Store release")
                  }}
                </td>
                <td class="px-4 py-3 text-gray-600 dark:text-gray-300 text-xs">
                  {{
                    release.has_artifact
                      ? formatFileSize(release.artifact_size_bytes)
                      : "—"
                  }}
                </td>
                <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                  {{ formatDate(release.created_at) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <hs-button
                      v-if="release.has_artifact"
                      variant="ghost"
                      size="sm"
                      icon-only
                      :icon="copiedReleaseId === release.id ? 'check' : 'link'"
                      @click="copyDownloadLink(release)"
                      :title="
                        copiedReleaseId === release.id
                          ? 'Copied!'
                          : 'Copy download link'
                      "
                    />
                    <hs-button
                      variant="ghost"
                      size="sm"
                      icon-only
                      icon="edit-2"
                      @click="editRelease(release)"
                      title="Edit"
                    />
                    <hs-button
                      variant="ghost"
                      size="sm"
                      icon-only
                      :icon="release.is_published ? 'eye-off' : 'eye'"
                      @click="togglePublish(release)"
                      :title="release.is_published ? 'Unpublish' : 'Publish'"
                    />
                    <hs-button
                      variant="ghost"
                      size="sm"
                      icon-only
                      icon="trash-2"
                      @click="confirmDelete(release)"
                      title="Delete"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700"
        >
          <span class="text-sm text-gray-500 dark:text-gray-400">
            {{ totalReleases }} release{{ totalReleases !== 1 ? "s" : "" }}
            total
          </span>
          <div class="flex gap-1">
            <button
              v-for="page in totalPages"
              :key="page"
              @click="goToPage(page)"
              class="rounded px-3 py-1 text-sm"
              :class="
                page === currentPage
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              "
            >
              {{ page }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Device Versions Tab -->
    <div v-show="activeTab === 'device-versions'" class="admin-releases__body">
      <device-versions-tab />
    </div>

    <!-- Upload Modal -->
    <upload-release-modal
      v-if="showUploadModal"
      @close-modal="showUploadModal = false"
      @release-uploaded="onReleaseUploaded"
    />

    <!-- Edit Modal -->
    <edit-release-modal
      v-if="showEditModal"
      :release="selectedRelease"
      @close-modal="showEditModal = false"
      @release-updated="onReleaseUpdated"
    />

    <hs-confirm-dialog
      v-model="showDeleteConfirm"
      title="Delete Release"
      :message="releaseToDelete ? `Delete release v${releaseToDelete.version} for ${targetLabel(releaseToDelete)} / ${releaseToDelete.platform}?` : 'Delete this release?'"
      confirm-text="Delete"
      tone="danger"
      @confirm="deleteSelectedRelease"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import UploadReleaseModal from "@/components/admin/releases/UploadReleaseModal.vue";
import EditReleaseModal from "@/components/admin/releases/EditReleaseModal.vue";
import DeviceVersionsTab from "@/components/admin/releases/DeviceVersionsTab.vue";
import { HsButton, HsConfirmDialog, HsMetricCard, HsSelect, HsTabs } from "@/components/ui";
import {
  RELEASE_FLAVORS,
  RELEASE_PRODUCTS,
  releaseTargetLabel,
} from "@/constants/releaseTargets";

export default {
  name: "AdminReleasesView",
  components: { UploadReleaseModal, EditReleaseModal, DeviceVersionsTab, HsButton, HsConfirmDialog, HsMetricCard, HsSelect, HsTabs },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      activeTab: "releases",
      tabs: [
        { id: "releases", label: "Releases", icon: "upload-cloud" },
        { id: "device-versions", label: "Device Versions", icon: "smartphone" },
      ],
      showUploadModal: false,
      showEditModal: false,
      selectedRelease: null,
      showDeleteConfirm: false,
      releaseToDelete: null,
      selectedProduct: "",
      selectedFlavor: "",
      selectedPlatform: "",
      copiedReleaseId: null,
      platforms: ["WINDOWS", "MAC", "LINUX", "ANDROID", "IOS"],
    };
  },
  computed: {
    ...mapGetters("adminReleases", [
      "releases",
      "totalReleases",
      "loadingReleases",
      "currentPage",
      "totalPages",
    ]),
    summaryStats() {
      const published = this.releases.filter((r) => r.is_published).length;
      const draft = this.releases.filter((r) => !r.is_published).length;
      const required = this.releases.filter((r) => r.is_required).length;
      return [
        { label: "Total Releases", value: this.totalReleases, icon: "upload-cloud", tone: "primary" },
        { label: "Published", value: published, icon: "check-circle", tone: "success" },
        { label: "Drafts", value: draft, icon: "edit-3", tone: "warning" },
        { label: "Required Updates", value: required, icon: "alert-circle", tone: "danger" },
      ];
    },
    platformOptions() {
      return [
        { label: "All Platforms", value: "" },
        ...this.platforms.map((platform) => ({ label: platform, value: platform })),
      ];
    },
    productFilterOptions() {
      return [{ label: "All Products", value: "" }, ...RELEASE_PRODUCTS];
    },
    flavorFilterOptions() {
      return [{ label: "All Flavors", value: "" }, ...RELEASE_FLAVORS];
    },
  },
  methods: {
    ...mapActions("adminReleases", [
      "fetchReleases",
      "updateRelease",
      "deleteRelease",
      "setPage",
      "setPlatformFilter",
      "setProductFilter",
      "setFlavorFilter",
    ]),
    refreshData() {
      this.fetchReleases();
    },
    onPlatformChange() {
      this.setPlatformFilter(this.selectedPlatform);
    },
    onProductChange() {
      this.setProductFilter(this.selectedProduct);
    },
    onFlavorChange() {
      this.setFlavorFilter(this.selectedFlavor);
    },
    goToPage(page) {
      this.setPage(page);
    },
    editRelease(release) {
      this.selectedRelease = { ...release };
      this.showEditModal = true;
    },
    async togglePublish(release) {
      try {
        await this.updateRelease({
          releaseId: release.id,
          payload: { is_published: !release.is_published },
        });
      } catch (e) {
        console.error("Failed to toggle publish:", e);
      }
    },
    async confirmDelete(release) {
      this.releaseToDelete = release;
      this.showDeleteConfirm = true;
    },
    async deleteSelectedRelease() {
      if (!this.releaseToDelete) return;
      try {
        await this.deleteRelease(this.releaseToDelete.id);
        this.showDeleteConfirm = false;
        this.releaseToDelete = null;
      } catch (e) {
        console.error("Failed to delete release:", e);
      }
    },
    onReleaseUploaded(result) {
      this.showUploadModal = false;
      this.fetchReleases();

      if (result?.device_version_error) {
        this.$toast?.warning?.(
          `Release created, but device version update failed: ${result.device_version_error}`,
        );
      } else if (result?.device_version_updated) {
        this.$toast?.success?.(
          `Release created — device version updated to ${
            result.device_version?.current_version || result.version
          }`,
        );
      }
    },
    onReleaseUpdated() {
      this.showEditModal = false;
      this.fetchReleases();
    },
    formatFileSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    platformBadgeClass(platform) {
      const map = {
        WINDOWS:
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        MAC: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
        LINUX:
          "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
        ANDROID:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        IOS: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      };
      return map[platform] || "bg-gray-100 text-gray-800";
    },
    targetLabel(release) {
      return releaseTargetLabel(release);
    },
    getDownloadUrl(release) {
      const base = process.env.VUE_APP_BACKEND_API || "";
      const root = base.replace(/\/api\/?$/, "");
      return `${root}/api/v2/updates/download/${release.uuid}`;
    },
    async copyDownloadLink(release) {
      const url = this.getDownloadUrl(release);
      try {
        await navigator.clipboard.writeText(url);
        this.copiedReleaseId = release.id;
        setTimeout(() => {
          this.copiedReleaseId = null;
        }, 2000);
      } catch {
        // Fallback for non-HTTPS
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        this.copiedReleaseId = release.id;
        setTimeout(() => {
          this.copiedReleaseId = null;
        }, 2000);
      }
    },
  },
  mounted() {
    this.fetchReleases();
  },
};
</script>

<style scoped>
.admin-releases {
  display: grid;
  gap: 1rem;
}

.admin-releases__tabs,
.admin-releases__body {
  display: grid;
  gap: 1rem;
}

.admin-releases__stats {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.admin-releases :deep(.rounded-xl),
.admin-releases :deep(.rounded-lg) {
  border-radius: 14px;
  box-shadow: none;
}

.admin-releases :deep(table) {
  border-collapse: collapse;
}

.admin-releases :deep(th) {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@media (max-width: 980px) {
  .admin-releases__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .admin-releases__stats {
    grid-template-columns: 1fr;
  }
}
</style>
